import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';
import { Search, MapPin, Grid, X } from 'lucide-react';
import { ProductCard } from '../components/layout/ProductCard';
import { ProductMap } from '../components/layout/ProductMap';
import { dummyProducts, DummyProduct } from '../lib/dummyProducts';

// Animated product card wrapper component for staggered scroll animations
const ProductCardWithAnimation: React.FC<{ product: DummyProduct; index: number }> = ({ product, index }) => {
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ${
        inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
      }`}
      style={{
        transitionDelay: `${(index % 15) * 40}ms`, // Stagger in batches of 15, 40ms delay each
      }}
    >
      <ProductCard
        id={product.id}
        name={product.product_name || product.name || 'Unknown Product'}
        price={product.price || 0}
        image={product.featured_image?.image || product.featured_image?.path || product.images?.[0] || product.image || '/placeholder-product.jpg'}
        isLatest={product.is_latest}
        origin={product.origin || product.location?.address || product.location?.farm_name}
        rating={product.average_rating || product.rating}
        reviewCount={product.review_count || product.reviewCount}
        category={typeof product.category === 'string' ? product.category : product.category?.name}
        isOrganic={product.is_organic !== false && product.isOrganic !== false}
      />
    </div>
  );
};

export const ProductPage: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All Products');
  const [viewMode, setViewMode] = useState<'grid' | 'map'>('grid');
  const [priceRange, setPriceRange] = useState({ min: 0, max: 2000 });
  const [sliderValue, setSliderValue] = useState(2000);
  const [sortBy, setSortBy] = useState<'featured' | 'price-low' | 'price-high' | 'newest' | 'rating'>('featured');

  // Use dummy products
  const products = useMemo(() => dummyProducts, []);

  // Extract unique categories from products
  const categories = useMemo(() => {
    if (!Array.isArray(products)) return [];
    const categorySet = new Set<string>();
    products.forEach((p) => {
      const catName = typeof p.category === 'string' ? p.category : p.category?.name;
      if (catName) categorySet.add(catName);
    });
    return Array.from(categorySet).map((name, idx) => ({ id: String(idx + 1), name }));
  }, [products]);

  // Calculate max price from products
  const maxPrice = useMemo(() => {
    if (!Array.isArray(products) || products.length === 0) return 2000;
    return Math.max(...products.map((p) => Number(p.price) || 0), 2000);
  }, [products]);

  // Update slider when maxPrice changes
  useEffect(() => {
    setPriceRange({ min: 0, max: maxPrice });
    setSliderValue(maxPrice);
  }, [maxPrice]);

  // Check if any filters are active
  const hasActiveFilters = useMemo(() => {
    return selectedCategory !== 'All Products' ||
           priceRange.min !== 0 ||
           priceRange.max !== maxPrice ||
           sortBy !== 'featured' ||
           searchTerm !== '';
  }, [selectedCategory, priceRange, maxPrice, sortBy, searchTerm]);

  // Clear all filters function
  const handleClearAllFilters = useCallback(() => {
    setSelectedCategory('All Products');
    setPriceRange({ min: 0, max: maxPrice });
    setSliderValue(maxPrice);
    setSortBy('featured');
    setSearchTerm('');
  }, [maxPrice]);

  // Update price range when slider changes
  const handleSliderChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = Number(e.target.value);
    setSliderValue(value);
    setPriceRange(prev => ({ ...prev, max: value }));
  }, []);

  // Filter and sort products
  const filteredProducts = useMemo(() => {
    if (!Array.isArray(products)) return [];

    // First, filter products
    let filtered = products.filter((product) => {
      const productName = (product.product_name || product.name || '').toLowerCase();
      const description = (product.description || '').toLowerCase();
      const searchLower = searchTerm.toLowerCase();

      const matchesSearch = productName.includes(searchLower) || description.includes(searchLower);
      const catName = typeof product.category === 'string' ? product.category : product.category?.name;
      const matchesCategory = selectedCategory === 'All Products' || selectedCategory === 'All' || catName === selectedCategory;
      const productPrice = Number(product.price) || 0;
      const matchesPrice = productPrice >= priceRange.min && productPrice <= priceRange.max;

      return matchesSearch && matchesCategory && matchesPrice;
    });

    // Then, sort products
    const sorted = [...filtered].sort((a, b) => {
      switch (sortBy) {
        case 'price-low':
          return (Number(a.price) || 0) - (Number(b.price) || 0);
        case 'price-high':
          return (Number(b.price) || 0) - (Number(a.price) || 0);
        case 'newest':
          return a.is_latest === b.is_latest ? 0 : a.is_latest ? -1 : 1;
        case 'rating':
          return (Number(b.average_rating || b.rating) || 0) - (Number(a.average_rating || a.rating) || 0);
        case 'featured':
        default:
          return 0;
      }
    });

    return sorted;
  }, [products, searchTerm, selectedCategory, priceRange, sortBy]);

  // Category options with product counts
  const categoryList = useMemo(() => {
    const allCount = products.length;
    const categoryCounts: Record<string, number> = {};

    products.forEach((product) => {
      const catName = typeof product.category === 'string' ? product.category : product.category?.name;
      if (catName) {
        categoryCounts[catName] = (categoryCounts[catName] || 0) + 1;
      }
    });

    return [
      { name: 'All Products', count: allCount },
      ...categories.map((cat) => ({
        name: cat.name,
        count: categoryCounts[cat.name] || 0
      }))
    ];
  }, [products, categories]);

  return (
    <main className="min-h-screen flex flex-col" style={{ backgroundColor: '#E8F5E9' }}>
      <Header />

      <div className="w-full px-4 sm:px-6 lg:px-8 pt-8 pb-12 flex-grow">
        {/* Search Bar - Top */}
        <div className="mb-8">
          <div className="relative max-w-2xl mx-auto">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search products..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-white border border-gray-200 rounded-xl transition-all shadow-sm focus:outline-none focus:ring-2 focus:shadow-md placeholder:text-gray-400"
              style={{
                borderColor: searchTerm ? '#2E7D32' : '#E5E7EB',
                color: '#1F2937'
              } as React.CSSProperties}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = '#2E7D32';
                e.currentTarget.style.boxShadow = '0 0 0 3px rgba(46, 125, 50, 0.1)';
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = searchTerm ? '#2E7D32' : '#E5E7EB';
                e.currentTarget.style.boxShadow = '0 1px 2px 0 rgba(0, 0, 0, 0.05)';
              }}
            />
          </div>
        </div>

        {/* Main Layout: Sidebar + Content */}
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar Filters */}
          <aside className="w-full lg:w-72 flex-shrink-0">
            <div className="bg-white rounded-2xl shadow-soft border border-gray-100 p-6 sticky top-24 space-y-6">
              {/* Filter Header with Clear Button */}
              {hasActiveFilters && (
                <div className="pb-4 border-b border-gray-100">
                  <button
                    onClick={handleClearAllFilters}
                    className="flex items-center gap-2 w-full px-4 py-2 bg-gray-50 hover:bg-gray-100 text-gray-700 rounded-lg transition-all duration-200 font-medium text-sm"
                  >
                    <X className="w-4 h-4" />
                    <span>Clear All Filters</span>
                  </button>
                </div>
              )}

              {/* Sort By Section */}
              <div className="pb-6 border-b border-gray-100">
                <h3 className="text-lg font-bold text-gray-900 mb-4 font-poppins">Sort By</h3>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as any)}
                  className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg transition-all font-medium text-gray-700 text-sm focus:outline-none"
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = '#2E7D32';
                    e.currentTarget.style.boxShadow = '0 0 0 3px rgba(46, 125, 50, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = '#E5E7EB';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <option value="featured">Featured</option>
                  <option value="price-low">Price: Low to High</option>
                  <option value="price-high">Price: High to Low</option>
                  <option value="newest">Newest First</option>
                  <option value="rating">Highest Rated</option>
                </select>
              </div>

              {/* Categories Section */}
              <div className="pb-6 border-b border-gray-100">
                <h3 className="text-lg font-bold text-gray-900 mb-4 font-poppins">Categories</h3>
                <ul className="space-y-1.5">
                  {categoryList.map((category) => (
                    <li key={category.name}>
                      <button
                        onClick={() => setSelectedCategory(category.name)}
                        className={`w-full text-left px-4 py-2.5 rounded-lg transition-all duration-200 font-medium flex items-center justify-between group ${
                          selectedCategory === category.name
                            ? 'text-white shadow-sm'
                            : 'text-gray-700 hover:bg-gray-50'
                        }`}
                        style={selectedCategory === category.name ? { backgroundColor: '#2E7D32' } : {}}
                      >
                        <span className="text-sm">{category.name}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          selectedCategory === category.name
                            ? 'bg-white/20 text-white'
                            : 'bg-gray-100 text-gray-600 group-hover:bg-gray-200'
                        }`}>
                          {category.count}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Price Range Section */}
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-4 font-poppins">Price Range</h3>
                <div className="space-y-4">
                  {/* Price Input Fields */}
                  <div className="flex items-center gap-3">
                    <div className="flex-1">
                      <label className="text-xs text-gray-600 mb-1 block">Min</label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">Rs</span>
                        <input
                          type="number"
                          min="0"
                          max={priceRange.max}
                          value={priceRange.min}
                          onChange={(e) => setPriceRange(prev => ({ ...prev, min: Number(e.target.value) }))}
                          className="w-full pl-9 pr-3 py-2 bg-gray-50 border border-gray-200 rounded-lg transition-all text-sm focus:outline-none"
                          style={{ color: '#374151' }}
                          onFocus={(e) => {
                            e.currentTarget.style.borderColor = '#2E7D32';
                            e.currentTarget.style.boxShadow = '0 0 0 3px rgba(46, 125, 50, 0.1)';
                          }}
                          onBlur={(e) => {
                            e.currentTarget.style.borderColor = '#E5E7EB';
                            e.currentTarget.style.boxShadow = 'none';
                          }}
                        />
                      </div>
                    </div>
                    <div className="flex-1">
                      <label className="text-xs text-gray-600 mb-1 block">Max</label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">Rs</span>
                        <input
                          type="number"
                          min={priceRange.min}
                          max={maxPrice}
                          value={sliderValue}
                          onChange={(e) => {
                            const value = Number(e.target.value);
                            setSliderValue(value);
                            setPriceRange(prev => ({ ...prev, max: value }));
                          }}
                          className="w-full pl-9 pr-3 py-2 bg-gray-50 border border-gray-200 rounded-lg transition-all text-sm focus:outline-none"
                          style={{ color: '#374151' }}
                          onFocus={(e) => {
                            e.currentTarget.style.borderColor = '#2E7D32';
                            e.currentTarget.style.boxShadow = '0 0 0 3px rgba(46, 125, 50, 0.1)';
                          }}
                          onBlur={(e) => {
                            e.currentTarget.style.borderColor = '#E5E7EB';
                            e.currentTarget.style.boxShadow = 'none';
                          }}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Slider */}
                  <div className="relative px-1">
                    <input
                      type="range"
                      min="0"
                      max={maxPrice}
                      value={sliderValue}
                      onChange={handleSliderChange}
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer price-slider"
                      style={{
                        background: `linear-gradient(to right, #2E7D32 0%, #2E7D32 ${(sliderValue / maxPrice) * 100}%, #E5E7EB ${(sliderValue / maxPrice) * 100}%, #E5E7EB 100%)`
                      }}
                    />
                  </div>

                  {/* Price Range Display */}
                  <div className="flex justify-between text-xs text-gray-500 px-1">
                    <span>Rs 0</span>
                    <span className="font-medium" style={{ color: '#2E7D32' }}>Rs {sliderValue}</span>
                  </div>
                </div>
              </div>
            </div>
          </aside>

          {/* Main Content Area */}
          <div className="flex-1">
            {/* Active Filters Display */}
            {(selectedCategory !== 'All Products' || searchTerm) && (
              <div className="mb-6 flex flex-wrap gap-2">
                {searchTerm && (
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium" style={{ backgroundColor: 'rgba(46, 125, 50, 0.1)', color: '#2E7D32' }}>
                    <span>Search: "{searchTerm}"</span>
                    <button
                      onClick={() => setSearchTerm('')}
                      className="rounded-full p-0.5 transition-colors"
                      style={{ backgroundColor: 'transparent' }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(46, 125, 50, 0.2)'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}
                {selectedCategory !== 'All Products' && (
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium" style={{ backgroundColor: 'rgba(46, 125, 50, 0.1)', color: '#2E7D32' }}>
                    <span>{selectedCategory}</span>
                    <button
                      onClick={() => setSelectedCategory('All Products')}
                      className="rounded-full p-0.5 transition-colors"
                      style={{ backgroundColor: 'transparent' }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(46, 125, 50, 0.2)'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* View Mode Toggle */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
              <div>
                <p className="text-gray-600 font-medium">
                  Showing <span className="font-semibold" style={{ color: '#2E7D32' }}>{filteredProducts?.length || 0}</span> of {products?.length || 0} products
                </p>
              </div>
              
              <div className="flex bg-white border border-gray-200 rounded-xl p-1 shadow-soft">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`px-4 py-2 rounded-lg transition-all ${
                    viewMode === 'grid'
                      ? 'text-white shadow-soft'
                      : 'text-gray-600'
                  }`}
                  style={viewMode === 'grid' ? { backgroundColor: '#2E7D32' } : {}}
                  onMouseEnter={(e) => {
                    if (viewMode !== 'grid') {
                      e.currentTarget.style.color = '#2E7D32';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (viewMode !== 'grid') {
                      e.currentTarget.style.color = '#717171';
                    }
                  }}
                >
                  <Grid className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewMode('map')}
                  className={`px-4 py-2 rounded-lg transition-all ${
                    viewMode === 'map'
                      ? 'text-white shadow-soft'
                      : 'text-gray-600'
                  }`}
                  style={viewMode === 'map' ? { backgroundColor: '#2E7D32' } : {}}
                  onMouseEnter={(e) => {
                    if (viewMode !== 'map') {
                      e.currentTarget.style.color = '#2E7D32';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (viewMode !== 'map') {
                      e.currentTarget.style.color = '#717171';
                    }
                  }}
                >
                  <MapPin className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Content */}
            {viewMode === 'grid' ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {Array.isArray(filteredProducts) && filteredProducts.length > 0 ? (
                  filteredProducts.map((product, index) => (
                    <ProductCardWithAnimation
                      key={product.id}
                      product={product}
                      index={index}
                    />
                  ))
                ) : (
                  <div className="col-span-full text-center py-20 bg-white rounded-2xl shadow-soft border border-gray-100">
                    <div className="text-gray-300 text-7xl mb-6">🔍</div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-3 font-poppins">No products found</h3>
                    <p className="text-gray-600 mb-6 text-lg">We couldn't find any products matching your criteria</p>
                    {hasActiveFilters && (
                      <button
                        onClick={handleClearAllFilters}
                        className="inline-flex items-center gap-2 px-6 py-3 text-white rounded-xl transition-all duration-200 font-medium shadow-sm hover:shadow-md"
                        style={{ backgroundColor: '#2E7D32' }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#4CAF50'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#2E7D32'}
                      >
                        <X className="w-4 h-4" />
                        <span>Clear All Filters</span>
                      </button>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <ProductMap
                products={Array.isArray(filteredProducts) ? filteredProducts : []}
                height="600px"
                className="w-full"
              />
            )}
          </div>
        </div>
      </div>

      <Footer />
    </main>
  );
};
